import React, { useState } from "react";
import toast from "react-hot-toast";

const NdrActionModal = ({ isOpen, onClose, order, onSubmit }) => {
    const [action, setAction] = useState("");
    const [comments, setComments] = useState("");
    const [scheduledDate, setScheduledDate] = useState("");
    const [deliverySlot, setDeliverySlot] = useState("");
    const [mobile, setMobile] = useState("");

    const [address, setAddress] = useState({
        CA1: "",
        CA2: "",
        CA3: "",
        CA4: "",
        pincode: "",
    });
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const isDelhivery = order?.provider === "Delhivery";
    const isEcomExpress = order?.provider === "EcomExpress";
    const isDtdc = order?.provider === "DTDC";
    const isAmazon = order?.provider === "Amazon";
    const isReattempt = action === "RE-ATTEMPT";
    const isReschedule = action === "RESCHEDULE";

    const handleSubmit = async () => {
        if (!action) {
            toast.error("Please select an instruction.");
            return;
        }

        const payload = {
            awb_number: order.awb_number,
            action,
        };

        if (isAmazon) {
            if (action === "RE-ATTEMPT") {
                if (!comments.trim()) return toast.error("Please enter your comments.");
                payload.comments = comments;
            } else if (action === "RESCHEDULE") {
                if (!scheduledDate) return toast.error("Please select a scheduled delivery date.");
                payload.rescheduleDate = scheduledDate;
            }
        } else {
            // For other providers
            if (!comments.trim()) {
                toast.error("Please enter your comments.");
                return;
            }

            payload.comments = comments;

            if (isEcomExpress && isReattempt) {
                if (!scheduledDate) return toast.error("Please select a scheduled delivery date.");
                if (!deliverySlot) return toast.error("Please select a delivery slot.");

                Object.assign(payload, {
                    scheduled_delivery_date: scheduledDate,
                    scheduled_delivery_slot: deliverySlot,
                    mobile,
                    consignee_address: address,
                });
            }

            if (isDtdc) {
                Object.assign(payload, {
                    customer_code: order.orderId || "",
                    rtoAction: action,
                    remarks: comments,
                });
            }
        }

        setLoading(true);
        try {
            await onSubmit(payload);
            onClose();
        } catch (error) {
            console.error("Error submitting NDR:", error);
            toast.error("Failed to submit action. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg w-[90%] max-w-md p-6 shadow-lg max-h-[90vh] overflow-y-auto">
                <h2 className="text-[16px] font-semibold mb-4">
                    Take Action for AWB: {order.awb_number}
                </h2>

                <div className="mb-4">
                    <label className="block text-[12px] font-medium mb-1">Instruction</label>
                    <select
                        value={action}
                        onChange={(e) => {
                            setAction(e.target.value);
                            setComments("");
                            setScheduledDate("");
                        }}
                        className="w-full border px-3 py-2 outline-none rounded"
                    >
                        <option value="">Select Instruction</option>
                        {isAmazon ? (
                            <>
                                <option value="RE-ATTEMPT">Re-attempt</option>
                                <option value="RESCHEDULE">Reschedule</option>
                                <option value="RTO">Return to Origin</option>
                            </>
                        ) : (
                            <>
                                <option value="RE-ATTEMPT">Re-attempt</option>
                                <option value="RTO">Return to Origin</option>
                            </>
                        )}
                    </select>
                </div>

                {/* Amazon-specific fields */}
                {isAmazon && action === "RE-ATTEMPT" && (
                    <div className="mb-4">
                        <label className="block text-[12px] font-medium mb-1">Comments</label>
                        <textarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows={3}
                            className="w-full border px-3 py-2 rounded"
                            placeholder="Enter comments"
                        />
                    </div>
                )}

                {isAmazon && action === "RESCHEDULE" && (
                    <div className="mb-4">
                        <label className="block text-[12px] font-medium mb-1">Scheduled Delivery Date</label>
                        <input
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                            className="w-full border px-3 py-2 rounded"
                        />
                    </div>
                )}

                {/* Ecom Express Only Fields */}
                {isEcomExpress && isReattempt && (
                    <>
                        <div className="mb-4">
                            <label className="block text-[12px] font-medium mb-1">Scheduled Delivery Date</label>
                            <input
                                type="date"
                                value={scheduledDate}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                className="w-full border px-3 py-2 rounded"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-[12px] font-medium mb-1">Delivery Slot</label>
                            <select
                                value={deliverySlot}
                                onChange={(e) => setDeliverySlot(e.target.value)}
                                className="w-full border px-3 py-2 rounded"
                            >
                                <option value="">Select Slot</option>
                                <option value="1">Morning</option>
                                <option value="2">Afternoon</option>
                                <option value="3">Evening</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-[12px] font-medium mb-1">Mobile (optional)</label>
                            <input
                                type="text"
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                                className="w-full border px-3 py-2 rounded"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-[12px] font-medium mb-1">Consignee Address (optional)</label>
                            <div className="grid grid-cols-2 gap-2">
                                {["CA1", "CA2", "CA3", "CA4"].map((field) => (
                                    <input
                                        key={field}
                                        type="text"
                                        placeholder={field}
                                        value={address[field]}
                                        onChange={(e) => setAddress({ ...address, [field]: e.target.value })}
                                        className="border px-3 py-2 rounded"
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-[12px] font-medium mb-1">Comments</label>
                            <textarea
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                rows={3}
                                className="w-full border outline-none px-3 py-2 rounded"
                                placeholder="Enter comments"
                            />
                        </div>
                    </>
                )}

                {/* Comments for non-Amazon providers, conditional for DTDC */}
                {!isAmazon && (!isDtdc || (isDtdc && isReattempt)) && (
                    <div className="mb-4">
                        <label className="block text-[12px] font-medium mb-1">Comments</label>
                        <textarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows={3}
                            className="w-full border outline-none px-3 py-2 rounded"
                            placeholder="Enter comments"
                        />
                    </div>
                )}



                <div className="flex justify-end space-x-2">
                    <button className="px-3 py-2 bg-gray-200 rounded" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="px-3 py-2 bg-[#0CBB7D] text-white rounded"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Submitting..." : "Submit"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NdrActionModal;
